<template>
    <div>
        <Loading :active.sync="loading" :isFullPage="false"></Loading>
        <div class="p-fluid p-formgrid p-grid">
            <div class="p-field p-col-12 p-md-2">
                <label for="date_location">Data da Carga <span class="p-text-danger">*</span></label>
                <Calendar
                    id="date_location"
                    v-model="localForm.date_location"
                    dateFormat="yy-mm-dd"
                    @input="updateField('date_location', $event)"
                    :disabled="readOnly"
                />
            </div>
            <div class="p-field p-col-12 p-md-2">
                <label for="time_location">Hora da Carga <span class="p-text-danger">*</span></label>
                <div class="p-inputgroup">
                    <span class="p-inputgroup-addon"><i class="pi pi-clock"></i></span>
                    <Calendar
                        id="time_location"
                        v-model="localForm.time_location"
                        timeOnly
                        @input="updateField('time_location', $event)"
                        :disabled="readOnly"
                    />
                </div>
            </div>
        </div>

        <div class="p-fluid p-formgrid p-grid">
            <div class="p-field p-col-12 p-md-6" v-for="locationType in locationTypes" :key="locationType.key">
                <div :class="['address-section', locationType.sectionClass, 'p-p-3 p-border-round shadow-1']">
                    <div class="p-d-flex p-jc-between p-ai-center p-flex-wrap p-mb-3">
                        <h3 class="p-m-0">
                            <i :class="['p-mr-2', locationType.icon, locationType.iconClass]"></i>
                            {{ locationType.label }} <span class="p-text-danger">*</span>
                        </h3>
                        <div class="p-d-flex p-ai-center p-flex-wrap" v-if="!readOnly">
                            <Button
                                v-for="company in companyAddresses"
                                :key="'comp-btn-' + locationType.key + '-' + company.id"
                                :label="company.label"
                                class="p-button-secondary p-button-info p-button-sm p-mr-2 p-mb-1 p-button-auto"
                                :class="{'p-button-outlined': selectedCompany[locationType.key] !== company.id}"
                                @click.prevent="fillWithCompanyAddress(locationType.key, company)"
                            />
                            <Button
                                v-if="patLocations.client"
                                label="Cliente"
                                class="p-button-secondary p-button-info p-button-sm p-mr-2 p-mb-1 p-button-auto"
                                :class="{'p-button-outlined': selectedPatButton[locationType.key] !== 'client'}"
                                @click.prevent="fillWithPatLocation(locationType.key, 'client')"
                            />
                            <Button
                                v-if="patLocations.entity"
                                label="Entidade"
                                class="p-button-secondary p-button-info p-button-sm p-mr-2 p-mb-1 p-button-auto"
                                :class="{'p-button-outlined': selectedPatButton[locationType.key] !== 'entity'}"
                                @click.prevent="fillWithPatLocation(locationType.key, 'entity')"
                            />
                        </div>
                    </div>

                    <div class="p-fluid p-formgrid p-grid p-mt-2">
                        <div class="p-field p-col-12 p-md-12">
                            <label>Origem</label>
                            <Dropdown
                                v-model="localValue[locationType.key].origem"
                                :name="`origin_${locationType.key}`"
                                :options="origins"
                                filter
                                dataKey="id"
                                optionLabel="label"
                                optionValue="value"
                                placeholder="Origem"
                                @change="() => resetLocation(locationType.key)"
                                appendTo="body"
                                :disabled="readOnly"
                            />
                        </div>

                        <div class="p-field p-col-12 p-md-12 p-mt-2">
                            <div v-show="localValue[locationType.key].origem === 'CL'">
                                <label>Selecionar cliente</label>
                                <AppAutocomplete
                                    :key="`inputClientId_${locationType.key}`"
                                    :fieldId="`inputClientId_${locationType.key}`"
                                    :optionLabel="getClientLabel"
                                    :editModel="getCurrentClient"
                                    :fetchFunction="fetchClients"
                                    optionValue="clientCompositeKey"
                                    v-model="localValue[locationType.key].clientCompositeKey"
                                    placeholder="pesquisar cliente"
                                    @selection-change="v => entityChange(locationType.key, v)"
                                    @clear-selection="() => resetLocation(locationType.key)"
                                    appendTo="body"
                                    :disabled="readOnly"
                                >
                                    <template #option="slotProps">
                                        <div>
                                            <b>{{ slotProps.option.name }}</b><br />
                                            <span style="white-space: normal; word-break: break-word;">
                                                <span v-if="slotProps.option.fiscalNumber">NIF: {{ slotProps.option.fiscalNumber }} | </span>
                                                <span v-if="slotProps.option.mobilPhone">TELM: {{ slotProps.option.mobilPhone }}</span>
                                            </span>
                                        </div>
                                    </template>
                                </AppAutocomplete>
                            </div>

                            <div v-show="localValue[locationType.key].origem === 'FL'">
                                <label>Selecionar fornecedor</label>
                                <AppAutocomplete
                                    :key="`inputSupplierId_${locationType.key}`"
                                    :fieldId="`inputSupplierId_${locationType.key}`"
                                    :optionLabel="getSupplierLabel"
                                    optionValue="supplierCompositeKey"
                                    v-model="localValue[locationType.key].supplierCompositeKey"
                                    :editModel="getCurrentSupplier"
                                    :fetchFunction="fetchSuppliers"
                                    @selection-change="v => entityChange(locationType.key, v)"
                                    @clear-selection="() => resetLocation(locationType.key)"
                                    appendTo="body"
                                    :disabled="readOnly"
                                >
                                    <template #option="slotProps">
                                        <div>
                                            <b>{{ slotProps.option.name }}</b><br />
                                            <span style="white-space: normal; word-break: break-word;">
                                                <span v-if="slotProps.option.nif">NIF: {{ slotProps.option.nif }}</span>
                                            </span>
                                        </div>
                                    </template>
                                </AppAutocomplete>
                            </div>

                            <div v-show="localValue[locationType.key].origem === ''">
                                <label>Selecionar Morada</label>
                                <AppAutocomplete
                                    :key="`inputLocation_${locationType.key}`"
                                    placeholder="pesquisar"
                                    :fieldId="`inputLocation_${locationType.key}`"
                                    :optionLabel="getLocationLabel"
                                    optionValue="szadrsdesc"
                                    v-model="localValue[locationType.key].szadrsdesc"
                                    :editModel="(v) => getCurrentLocation(v)"
                                    :fetchFunction="(search) => fetchOtherLocations(search)"
                                    @selection-change="v => changeOtherLocation(locationType.key, v)"
                                    @clear-selection="() => clearOtherLocation(locationType.key)"
                                    appendTo="body"
                                    :disabled="readOnly"
                                >
                                    <template #option="slotProps">
                                        <div>
                                            <b>{{ slotProps.option.szadrsdesc }}</b><br />
                                            <span class="text-sm">{{ slotProps.option.morada }} | {{ slotProps.option.codpost }}</span>
                                        </div>
                                    </template>
                                </AppAutocomplete>
                            </div>
                        </div>

                        <div class="p-field p-col-12 p-md-12 p-mt-3" v-if="localValue[locationType.key].locations.length > 0">
                            <span class="p-float-label">
                                <Dropdown
                                    v-model="localValue[locationType.key].szadrsdesc"
                                    :name="`address_${locationType.key}`"
                                    :options="localValue[locationType.key].locations"
                                    filter
                                    dataKey="szadrsdesc"
                                    optionLabel="szadrsdesc"
                                    optionValue="szadrsdesc"
                                    @change="fillAddressFieldsFromDropdown(locationType.key)"
                                    appendTo="body"
                                    :disabled="readOnly"
                                />
                                <label>Selecionar local</label>
                            </span>
                        </div>

                        <div class="p-field p-col-12 p-md-12 p-mt-2" v-if="localValue[locationType.key].szadrsdesc || localValue[locationType.key].entity != null">
                            <div class="p-d-flex p-ai-center p-jc-between">
                                <label>
                                    Informações de Endereço
                                </label>

                                <div v-if="!readOnly">
                                    <template>
                                        <Button
                                            v-if="!localValue[locationType.key].locations.length && (localValue[locationType.key].origem !== '' || localValue[locationType.key].entity != null)"
                                            icon="pi pi-plus"
                                            label="Nova Morada"
                                            class="p-button-sm p-button-success"
                                            @click="openAddressDialog(locationType.key, 'create')"
                                        />
                                        <template v-else>
                                            <Button
                                                icon="pi pi-plus"
                                                class="p-button-sm p-button-success p-mr-2"
                                                @click="openAddressDialog(locationType.key, 'create')"
                                            />
                                            <Button
                                                icon="pi pi-pencil"
                                                class="p-button-sm p-button-info p-mr-2"
                                                @click="openAddressDialog(locationType.key, 'edit')"
                                            />
                                            <Button
                                                icon="pi pi-eye"
                                                class="p-button-sm p-button-secondary"
                                                @click="openAddressDialog(locationType.key, 'preview')"
                                            />
                                        </template>
                                    </template>
                                </div>
                            </div>

                            <div v-if="localValue[locationType.key].szadrsdesc" class="p-mt-2 p-p-2" style="background: #f8f9fa; border-radius: 4px; border: 1px solid #dee2e6;">
                                <strong>{{ localValue[locationType.key].nome }}</strong><br>
                                <small>{{ localValue[locationType.key].morada }}, {{ localValue[locationType.key].local }}, {{ localValue[locationType.key].codpost }}, {{ localValue[locationType.key].pais }}</small>
                            </div>
                            <div v-else class="p-mt-2 p-p-2" style="background: #f8f9fa; border-radius: 4px; border: 1px solid #dee2e6;">
                                <label v-if="!localValue[locationType.key].locations.length && (localValue[locationType.key].origem !== '' || localValue[locationType.key].entity != null)">
                                    Sem informações de endereço, deve registar uma nova morada para este cliente/entidade/fornecedor
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="hasPat && !readOnly" class="p-d-flex p-ai-center p-flex-wrap p-mt-3 p-mb-2">
            <div class="p-mr-2">
                <Button
                    label="Preencher com Equipamento"
                    icon="pi pi-desktop"
                    class="p-button-secondary p-button-info p-button-sm p-button-outlined"
                    :loading="loadingEquipment"
                    @click.prevent="fillWithEquipment"
                />
            </div>
            <div>
                <Button
                    label="Peças"
                    icon="pi pi-cog"
                    class="p-button-secondary p-button-info p-button-sm p-button-outlined"
                    :loading="loadingParts"
                    @click.prevent="openPartsDialog"
                />
            </div>
        </div>

        <Dialog
            header="Peças do PAT (Trf entre Armazéns)"
            :visible.sync="showPartsDialog"
            :style="{ width: $isMobile() ? '100vw' : '850px', maxWidth: '95vw' }"
            :modal="true"
            :closable="true"
        >
            <div v-if="availableParts.length">
                <p class="p-text-secondary p-mb-2">
                    Selecione as peças transferidas para este PAT a adicionar à Guia de Transporte:
                </p>
                <DataTable
                    :value="availableParts"
                    :selection.sync="selectedParts"
                    dataKey="uniqueKey"
                    responsiveLayout="scroll"
                    class="p-datatable-sm"
                >
                    <Column selectionMode="multiple" headerStyle="width: 3.5em"></Column>
                    <Column field="product_ref" header="Referência" :sortable="true" headerStyle="width: 140px;"></Column>
                    <Column field="description" header="Descrição" :sortable="true"></Column>
                    <Column field="quantity" header="Qtd." headerStyle="width: 70px; text-align: right;" bodyStyle="text-align: right;"></Column>
                    <Column header="Armazém Origem" headerStyle="width: 160px;">
                        <template #body="slotProps">
                            {{ slotProps.data.warehouse_origin_name ? `Arm. ${slotProps.data.warehouse_origin_name}` : '-' }}
                        </template>
                    </Column>
                    <Column header="Armazém Destino" headerStyle="width: 160px;">
                        <template #body="slotProps">
                            {{ slotProps.data.destination_warehouse_name ? `Arm. ${slotProps.data.destination_warehouse_name}` : '-' }}
                        </template>
                    </Column>
                    <Column header="Doc. Trf" headerStyle="width: 100px; text-align: center;" bodyStyle="text-align: center;">
                        <template #body="slotProps">
                            #{{ slotProps.data.dossier_id }}
                        </template>
                    </Column>
                </DataTable>
            </div>
            <template #footer>
                <Button
                    label="Cancelar"
                    icon="pi pi-times"
                    class="p-button-text"
                    @click="showPartsDialog = false"
                />
                <Button
                    label="Adicionar às Linhas"
                    icon="pi pi-check"
                    class="p-button-success"
                    :disabled="!selectedParts || !selectedParts.length"
                    @click="confirmAddParts"
                />
            </template>
        </Dialog>

        <Dialog
            :header="dialogTitle"
            :visible.sync="displayDialog"
            :style="{ width: $isMobile() ? '100vw' : '60vw' }"
            :modal="true"
            :closable="true"
        >
            <div class="p-fluid p-formgrid p-grid p-pt-2" v-if="activeAddress">
                <div class="p-field p-col-12 p-mt-3">
                    <span class="p-float-label">
                        <InputText v-model="activeAddress.szadrsdesc" :disabled="['preview', 'edit'].includes(dialogMode)" />
                        <label>Descrição</label>
                    </span>
                </div>
                <div class="p-field p-col-12 p-mt-3">
                    <span class="p-float-label">
                        <InputText v-model="activeAddress.nome" :disabled="dialogMode === 'preview'" />
                        <label>Nome</label>
                    </span>
                </div>
                <div class="p-field p-col-12 p-mt-3">
                    <span class="p-float-label">
                        <InputText v-model="activeAddress.morada" :disabled="dialogMode === 'preview'" />
                        <label>Morada</label>
                    </span>
                </div>
                <div class="p-field p-col-6 p-mt-3">
                    <span class="p-float-label">
                        <InputText v-model="activeAddress.local" :disabled="dialogMode === 'preview'" />
                        <label>Local</label>
                    </span>
                </div>
                <div class="p-field p-col-6 p-mt-3">
                    <span class="p-float-label">
                        <InputText v-model="activeAddress.codpost" :disabled="dialogMode === 'preview'" />
                        <label>Código Postal</label>
                    </span>
                </div>
                <div class="p-field p-col-6 p-mt-3">
                    <span class="p-float-label">
                        <InputText v-model="activeAddress.pais" :disabled="dialogMode === 'preview'" />
                        <label>País</label>
                    </span>
                </div>
            </div>
            <template #footer>
                <Button
                    v-if="localValue[activeLocationType] && localValue[activeLocationType].entity && dialogMode === 'create'"
                    :label="localValue[activeLocationType].origem === 'CL' ? 'Preencher com valores do cliente' : 'Preencher com valores da entidade'"
                    icon="pi pi-user"
                    @click.prevent="fillWithEntityValues"
                    class="p-button-text p-button-secondary"
                />
                <Button label="Fechar" icon="pi pi-times" @click="displayDialog = false" class="p-button-text" />
                <Button
                    v-if="dialogMode !== 'preview'"
                    :disabled="loading"
                    label="Confirmar"
                    icon="pi pi-check"
                    @click.prevent="saveAddress"
                    autofocus
                />
            </template>
        </Dialog>
    </div>
</template>

<script>
import AppAutocomplete from '@/components/elements/AppAutocomplete.vue';
import transportDocumentService from '@/services/transport-document.service';
import clientService from '@/services/client.service';
import supplierService from '@/services/supplier.service';
import patService from '@/services/pat.service';
import internalDossierService from '@/services/internalDossier.service';
import Loading from 'vue-loading-overlay';

const emptyLocationState = () => ({
    origem: 'CL',
    szadrsdesc: null,
    no: null,
    estab: null,
    entity: null,
    clientCompositeKey: null,
    supplierCompositeKey: null,
    nome: null,
    morada: null,
    local: null,
    codpost: null,
    pais: null,
    locations: []
});

export const COMPANY_ADDRESSES = [
    {
        id: 'sintra',
        label: 'SINTRA',
        szadrsdesc: 'CIRCUIT CRAFTERS LDA'
    },
    {
        id: 'lisboa',
        label: 'MAIA',
        szadrsdesc: 'CIRCUIT CRAFTERS MAIA.'
    }
];

export default {
    name: 'AppTransportDocumentField',
    components: { AppAutocomplete, Loading },

    props: {
        form: { type: Object, default: () => ({}) },
        readOnly: { type: Boolean, default: false }
    },

    data() {
        return {
            loading: false,
            localValue: {
                load_location: emptyLocationState(),
                unload_location: emptyLocationState()
            },
            selectedCompany: {
                load_location: null,
                unload_location: null
            },
            selectedPatButton: {
                load_location: null,
                unload_location: null
            },
            patLocations: {
                client: null,
                entity: null
            },
            currentPatId: null,
            companyAddresses: COMPANY_ADDRESSES,
            origins: [
                { label: 'Cliente', value: 'CL' },
                { label: 'Fornecedor', value: 'FL' },
                { label: 'Outro', value: '' }
            ],
            locationTypes: [
                {
                    key: 'load_location',
                    label: 'Local de Carga',
                    sectionClass: 'load-section',
                    icon: 'pi pi-upload',
                    iconClass: 'text-primary'
                },
                {
                    key: 'unload_location',
                    label: 'Local de Descarga',
                    sectionClass: 'unload-section',
                    icon: 'pi pi-download',
                    iconClass: 'text-warning'
                }
            ],
            displayDialog: false,
            dialogMode: 'preview', // 'create' | 'edit' | 'preview'
            activeLocationType: 'load_location',
            activeAddress: null,
            patInfo: null,
            loadingEquipment: false,
            loadingParts: false,
            showPartsDialog: false,
            availableParts: [],
            selectedParts: []
        };
    },

    computed: {
        localForm() {
            return this.form || {};
        },
        dialogTitle() {
            const modes = { create: 'Criar Morada', edit: 'Editar Morada', preview: 'Visualizar Morada' };
            return modes[this.dialogMode] || 'Morada';
        },
        hasPat() {
            return Boolean(this.currentPatId || (this.form && this.form.pat_id));
        },
        effectivePatId() {
            const pid = this.currentPatId || (this.form && this.form.pat_id);
            return pid ? parseInt(pid, 10) : null;
        }
    },

    watch: {
        form: {
            handler(newVal) {
                if (!newVal) return;
                const patId = newVal.pat_id;
                if (patId !== this.currentPatId) {
                    this.currentPatId = patId;
                    this.loadPatLocations(patId);
                }
                ['load_location', 'unload_location'].forEach(type => {
                    if (newVal[type] && !this.localValue[type].szadrsdesc) {
                        this.localValue[type].szadrsdesc = newVal[type];
                    }
                    this.detectSelectedPrefill(type);
                });
            },
            immediate: true,
            deep: true
        }
    },

    async created() {
        if (!this.form) return;

        if (!this.localForm.time_location && !this.readOnly) {
            const defaultTime = new Date();
            defaultTime.setMinutes(defaultTime.getMinutes() + 5);
            this.$set(this.localForm, 'time_location', defaultTime);
            this.updateField('time_location', defaultTime);
        }
        if (!this.localForm.date_location && !this.readOnly) {
            const defaultDate = new Date();
            this.$set(this.localForm, 'date_location', defaultDate);
            this.updateField('date_location', defaultDate);
        }

        this.loading = true;
        try {
            await Promise.all(
                ['load_location', 'unload_location'].map(type => this.initLocation(type))
            );
        } finally {
            this.loading = false;
        }
    },

    methods: {

        updateField(key, value) {
            this.$emit('field-change', { key, value });
        },

        getLocationLabel(location) {
            if (!location) return '';
            return `${location.szadrsdesc} | ${location.morada} | ${location.codpost}`;
        },

        getClientLabel(client) {
            if (!client) return '';
            return `${client.name} | EMAIL: ${client.email} | NIF: ${client.fiscalNumber} | TEL.: ${client.telephone} | TELM.: ${client.mobilPhone}`;
        },

        getSupplierLabel(supplier) {
            if (!supplier) return '';
            return `${supplier.name} | NIF: ${supplier.nif ?? supplier.fiscalNumber}`;
        },

        setAddressFields(type, data) {
            const fields = ['szadrsstamp', 'szadrsdesc', 'nome', 'morada', 'local', 'codpost', 'pais', 'estab', 'no'];
            fields.forEach(f => { this.localValue[type][f] = data[f] ?? null; });
            if (data.origem !== undefined) {
                this.localValue[type].origem = data.origem;
            }
        },

        syncAddressToParent(type) {
            const fields = ['', '_nome', '_morada', '_local', '_codpost', '_pais', '_no', '_estab', '_origem'];
            const loc = this.localValue[type];
            const map = { '': loc.szadrsdesc, '_nome': loc.nome, '_morada': loc.morada,
                '_local': loc.local, '_codpost': loc.codpost, '_pais': loc.pais,
                '_no': loc.no, '_estab': loc.estab, '_origem': loc.origem };
            fields.forEach(suffix => this.updateField(type + suffix, map[suffix]));
        },

        fillAddressFieldsFromDropdown(type) {
            this.selectedCompany[type] = null;
            this.selectedPatButton[type] = null;
            const selectedDesc = this.localValue[type].szadrsdesc;
            const selectedLoc = this.localValue[type].locations.find(l => l.szadrsdesc === selectedDesc);
            if (selectedLoc) {
                this.setAddressFields(type, selectedLoc);
                this.syncAddressToParent(type);
                this.detectSelectedPrefill(type);
            }
        },

        async loadPatLocations(patId) {
            if (!patId) {
                this.patInfo = null;
                this.patLocations = { client: null, entity: null };
                ['load_location', 'unload_location'].forEach(type => {
                    this.detectSelectedPrefill(type);
                });
                return;
            }
            try {
                const info = await patService.getInfo(patId);
                this.patInfo = info;
                if (info) {
                    const client = info.client ? {
                        ...info.client,
                        country: info.client.country || 'Portugal',
                        zipCode: info.client.zipCode || info.client.zip_code || '',
                        local: info.client.city || info.client.local || '',
                        morada: info.client.address || info.client.morada || '',
                        nome: info.client.name || info.client.nome || ''
                    } : null;

                    const entity = info.entity && (info.entity.name || info.entity.nome) ? {
                        ...info.entity,
                        nome: info.entity.name || info.entity.nome,
                        name: info.entity.name || info.entity.nome,
                        morada: info.entity.address || info.entity.morada,
                        address: info.entity.address || info.entity.morada,
                        local: info.entity.city || info.entity.local,
                        city: info.entity.city || info.entity.local,
                        country: info.entity.country || info.entity.pais || 'Portugal',
                        zipCode: info.entity.zip_code || info.entity.zipCode || ''
                    } : null;

                    this.patLocations = { client, entity };
                    ['load_location', 'unload_location'].forEach(type => {
                        this.detectSelectedPrefill(type);
                    });
                } else {
                    this.patLocations = { client: null, entity: null };
                }
            } catch (err) {
                console.error('Error fetching PAT locations:', err);
                this.patInfo = null;
                this.patLocations = { client: null, entity: null };
            }
        },

        async fillWithPatLocation(type, target) {
            if (target === 'client') {
                const client = this.patLocations.client;
                if (!client) return;

                this.selectedCompany[type] = null;
                this.selectedPatButton[type] = 'client';

                const clientId = client.id || client.no;
                const clientEstab = client.estab ?? 0;
                const compositeKey = `${clientId}-${clientEstab}`;

                this.localValue[type].origem = 'CL';
                this.localValue[type].clientCompositeKey = compositeKey;

                await this.entityChange(type, compositeKey);
                this.selectedPatButton[type] = 'client';

                if (!this.localValue[type].locations.length) {
                    this.$toast.add({
                        severity: 'info',
                        summary: 'Aviso',
                        detail: 'O cliente não possui morada registada. Pode registar uma nova morada com os dados do PAT.',
                        life: 4000
                    });
                }
            } else if (target === 'entity') {
                const entity = this.patLocations.entity;
                if (!entity) return;

                this.selectedCompany[type] = null;
                this.selectedPatButton[type] = 'entity';

                let location = null;
                try {
                    this.loading = true;
                    if (entity.name) {
                        location = await this.getCurrentLocation(entity.name);
                    }
                    if (!location && entity.id) {
                        const agLocations = await this.fetchLocations({ origem: 'AG', no: entity.id });
                        if (agLocations.length) {
                            location = agLocations[0];
                        }
                    }
                } catch (err) {
                    console.error('Error fetching entity location:', err);
                } finally {
                    this.loading = false;
                }

                if (location) {
                    const origem = location.origem !== undefined ? location.origem : '';
                    this.localValue[type].origem = origem;
                    this.localValue[type].no = location.no ?? 0;
                    this.localValue[type].estab = location.estab ?? 0;

                    if (origem === 'CL' && location.no) {
                        this.localValue[type].clientCompositeKey = `${location.no}-${location.estab ?? 0}`;
                        this.localValue[type].supplierCompositeKey = null;
                    } else if (origem === 'FL' && location.no) {
                        this.localValue[type].supplierCompositeKey = `${location.no}-${location.estab ?? 0}`;
                        this.localValue[type].clientCompositeKey = null;
                    } else {
                        this.localValue[type].clientCompositeKey = null;
                        this.localValue[type].supplierCompositeKey = null;
                    }

                    this.localValue[type].entity = entity;
                    this.localValue[type].locations = [location];
                    this.setAddressFields(type, location);
                    this.syncAddressToParent(type);
                } else {
                    this.localValue[type].origem = '';
                    this.localValue[type].no = 0;
                    this.localValue[type].estab = 0;
                    this.localValue[type].clientCompositeKey = null;
                    this.localValue[type].supplierCompositeKey = null;
                    this.localValue[type].locations = [];
                    this.clearOtherLocation(type);
                    this.localValue[type].origem = '';
                    this.localValue[type].entity = entity;
                    this.$toast.add({
                        severity: 'info',
                        summary: 'Aviso',
                        detail: 'A entidade não possui morada registada. Pode registar uma nova morada com os dados do PAT.',
                        life: 4000
                    });
                }
            }
        },

        async fillWithCompanyAddress(type, company) {
            let location = null;
            try {
                this.loading = true;
                location = await this.getCurrentLocation(company.szadrsdesc);
            } catch (err) {
                console.error('Error fetching company address:', err);
            } finally {
                this.loading = false;
            }

            if (!location) {
                this.$toast.add({
                    severity: 'warn',
                    summary: 'Aviso',
                    detail: `Não existe morada com o nome "${company.szadrsdesc}"`,
                    life: 4000
                });
                return;
            }

            this.selectedPatButton[type] = null;
            this.selectedCompany[type] = company.id || company.szadrsdesc;

            const origem = location.origem !== undefined ? location.origem : '';

            this.localValue[type].origem = origem;
            this.localValue[type].no = location.no ?? 0;
            this.localValue[type].estab = location.estab ?? 0;

            if (origem === 'CL' && location.no) {
                const no = location.no;
                const estab = location.estab ?? 0;
                this.localValue[type].clientCompositeKey = `${no}-${estab}`;
                this.localValue[type].supplierCompositeKey = null;
                this.localValue[type].entity = await this.getCurrentClient(this.localValue[type].clientCompositeKey);
            } else if (origem === 'FL' && location.no) {
                const no = location.no;
                const estab = location.estab ?? 0;
                this.localValue[type].supplierCompositeKey = `${no}-${estab}`;
                this.localValue[type].clientCompositeKey = null;
                this.localValue[type].entity = await this.getCurrentSupplier(this.localValue[type].supplierCompositeKey);
            } else {
                this.localValue[type].clientCompositeKey = null;
                this.localValue[type].supplierCompositeKey = null;
                this.localValue[type].entity = null;
            }

            this.localValue[type].locations = [location];

            this.setAddressFields(type, location);
            this.syncAddressToParent(type);
        },

        detectSelectedPrefill(type) {
            this.detectSelectedCompany(type);
            this.detectSelectedPat(type);
        },

        detectSelectedCompany(type) {
            const currentLoc = this.localValue[type];
            if (!currentLoc || !currentLoc.szadrsdesc) {
                this.selectedCompany[type] = null;
                return;
            }
            const match = this.companyAddresses.find(comp => this.isCompanyMatch(currentLoc, comp));
            this.selectedCompany[type] = match ? (match.id || match.szadrsdesc) : null;
            if (this.selectedCompany[type]) {
                this.selectedPatButton[type] = null;
            }
        },

        detectSelectedPat(type) {
            const currentLoc = this.localValue[type];
            if (!currentLoc || !currentLoc.szadrsdesc) {
                this.selectedPatButton[type] = null;
                return;
            }
            if (this.selectedCompany[type]) {
                this.selectedPatButton[type] = null;
                return;
            }
            if (this.patLocations.client && this.isPatClientMatch(currentLoc, this.patLocations.client)) {
                this.selectedPatButton[type] = 'client';
                return;
            }
            if (this.patLocations.entity && this.isPatEntityMatch(currentLoc, this.patLocations.entity)) {
                this.selectedPatButton[type] = 'entity';
                return;
            }
            this.selectedPatButton[type] = null;
        },

        isPatClientMatch(loc, client) {
            if (!loc || !client) return false;
            const clientId = client.id || client.no;
            if (loc.origem === 'CL' && clientId && String(loc.no) === String(clientId)) {
                return true;
            }
            const normalize = val => String(val || '').trim().toLowerCase();
            return Boolean(loc.nome && client.name && normalize(loc.nome) === normalize(client.name));
        },

        isPatEntityMatch(loc, entity) {
            if (!loc || !entity) return false;
            const normalize = val => String(val || '').trim().toLowerCase();
            if (loc.szadrsdesc && entity.name && normalize(loc.szadrsdesc) === normalize(entity.name)) {
                return true;
            }
            return Boolean(loc.nome && entity.name && normalize(loc.nome) === normalize(entity.name));
        },

        isCompanyMatch(a, b) {
            if (!a || !b) return false;
            const normalize = val => String(val || '').trim().toLowerCase();
            return normalize(a.szadrsdesc) === normalize(b.szadrsdesc);
        },

        async fetchLocations(parameters = {}) {
            return await transportDocumentService.query({ ...parameters });
        },

        async fetchClients(search = '') {
            try {
                return await clientService.query({ search });
            } catch (error) {
                console.error('Error fetching clients:', error);
                throw error;
            }
        },

        async fetchSuppliers(search = '') {
            try {
                return await supplierService.query({ search });
            } catch (error) {
                console.error('Error fetching suppliers:', error);
                return [];
            }
        },

        async getCurrentClient(compositeValue) {
            try {
                const [id, estab = 0] = (compositeValue ?? '').split('-');
                const response = await clientService.query({ no: id, estab });
                if (!response.length) return null;
                return { ...response[0], compositeKey: `${response[0].id}-${response[0].estab}` };
            } catch (error) {
                console.error('Error fetching client:', error);
                return null;
            }
        },

        async getCurrentSupplier(compositeValue) {
            try {
                const [id, estab = 0] = (compositeValue ?? '').split('-');
                const response = await supplierService.query({ no: id, estab });
                if (!response.length) return null;
                return { ...response[0], compositeKey: `${response[0].id}-${response[0].estab}` };
            } catch (error) {
                console.error('Error fetching supplier:', error);
                return null;
            }
        },

        async getCurrentLocation(szadrsdesc) {
            if (!szadrsdesc) return null;
            const locations = await transportDocumentService.query({
                szadrsdesc: encodeURIComponent(szadrsdesc.trim())
            });
            return locations.length ? locations[0] : null;
        },

        async initLocation(type) {
            const value = this.form[type];
            if (!value) return;

            this.localValue[type].szadrsdesc = value;
            const location = await this.getCurrentLocation(value);
            if (!location) return;

            this.localValue[type].origem = location.origem;

            if (location.origem === 'CL') {
                this.localValue[type].clientCompositeKey = `${location.no}-${location.estab}`;
                await this.entityChange(type, this.localValue[type].clientCompositeKey);
            } else if (location.origem === 'FL') {
                this.localValue[type].supplierCompositeKey = `${location.no}-${location.estab}`;
                await this.entityChange(type, this.localValue[type].supplierCompositeKey);
            } else {
                this.localValue[type].clientCompositeKey = null;
                this.localValue[type].supplierCompositeKey = null;
                await this.changeOtherLocation(type, value);
            }

            this.setAddressFields(type, location);
            this.detectSelectedPrefill(type);
        },

        async entityChange(type, compositeValue) {
            if (!compositeValue) return;

            this.selectedCompany[type] = null;
            this.selectedPatButton[type] = null;
            this.clearOtherLocation(type);

            const [id, estab] = compositeValue.split('-');
            const origem = this.localValue[type].origem;

            this.localValue[type].no = id;
            this.localValue[type].estab = estab;

            const entity = origem === 'CL'
                ? await this.getCurrentClient(compositeValue)
                : await this.getCurrentSupplier(compositeValue);

            this.localValue[type].entity = entity;

            const locations = await this.fetchLocations({ origem, no: id });

            if (locations.length) {
                this.localValue[type].locations = locations;
                this.setAddressFields(type, locations[0]);
                this.syncAddressToParent(type);
                this.detectSelectedPrefill(type);
            } else {
                this.localValue[type].locations = [];
            }
        },

        resetLocation(type) {
            this.selectedCompany[type] = null;
            this.selectedPatButton[type] = null;
            this.localValue[type].no = null;
            this.localValue[type].estab = null;
            this.localValue[type].entity = null;
            this.localValue[type].supplierCompositeKey = null;
            this.localValue[type].clientCompositeKey = null;
            this.clearOtherLocation(type);
            this.syncAddressToParent(type);
        },

        async fetchOtherLocations(search = '') {
            return await transportDocumentService.query({ search, origem: '' });
        },

        async changeOtherLocation(type, val) {
            if (!val) return;
            const row = await this.getCurrentLocation(val);
            if (row) {
                this.localValue[type].locations = [];
                this.setAddressFields(type, row);
                this.syncAddressToParent(type);
                this.detectSelectedPrefill(type);
            }
        },

        clearOtherLocation(type) {
            this.selectedCompany[type] = null;
            this.selectedPatButton[type] = null;
            this.localValue[type].locations = [];
            this.setAddressFields(type, {
                szadrsstamp: null, szadrsdesc: '', nome: '', morada: '', local: '', codpost: '', pais: ''
            });
            this.syncAddressToParent(type);
        },

        openAddressDialog(type, mode) {
            this.activeLocationType = type;
            this.dialogMode = mode;

            this.activeAddress = mode === 'create'
                ? { szadrsstamp: null, szadrsdesc: '', nome: '', morada: '', local: '', codpost: '', pais: 'Portugal' }
                : { ...this.localValue[type] };

            if (mode === 'create' && this.localValue[type]?.entity) {
                this.fillWithEntityValues();
            }
            this.displayDialog = true;
        },

        async saveAddress() {
            if (!['create', 'edit'].includes(this.dialogMode)) return;

            const saved = await this.updateOrCreateTransportDocument();
            if (saved) {
                this.syncAddressToParent(this.activeLocationType);
                this.displayDialog = false;
            }
        },

        async updateOrCreateTransportDocument() {
            try {
                this.loading = true;
                const loc = this.localValue[this.activeLocationType];
                let hasNoOrigin = loc.origem === '';

                const payload = {
                    szadrsstamp: this.activeAddress.szadrsstamp,
                    szadrsdesc: this.activeAddress.szadrsdesc,
                    nome: this.activeAddress.nome,
                    morada: this.activeAddress.morada,
                    local: this.activeAddress.local,
                    codpost: this.activeAddress.codpost,
                    pais: this.activeAddress.pais,
                    origem: loc.origem,
                    no: hasNoOrigin ? 0 : loc.no,
                    estab: hasNoOrigin ? 0 : loc.estab
                };

                const response = await transportDocumentService.updateOrCreateTransportDocument(payload);

                if (response.status === 422) {
                    this.$toast.add({ severity: 'error', summary: 'Erro', detail: response.data.message || 'Erro de validação', life: 3000 });
                    return false;
                }
                const savedData = response?.data?.data;
                if (savedData?.szadrsdesc) {
                    const locations = this.localValue[this.activeLocationType].locations;
                    const index = locations.findIndex(l => l.szadrsstamp === this.activeAddress.szadrsstamp);

                    index !== -1
                        ? locations.splice(index, 1, savedData)
                        : locations.push(savedData);

                    this.setAddressFields(this.activeLocationType, savedData);
                    this.detectSelectedPrefill(this.activeLocationType);
                    this.$toast.add({ severity: 'success', summary: 'Sucesso', detail: 'Morada atualizada e selecionada', life: 3000 });
                    return true;
                }

                this.$toast.add({ severity: 'error', summary: 'Erro', detail: 'Morada atualizada mas ocorreu um erro ao selecionar', life: 3000 });
                return false;

            } catch (error) {
                console.error('Error updating location:', error);
                this.$toast.add({ severity: 'error', summary: 'Erro', detail: 'Ocorreu um erro ao atualizar a morada', life: 3000 });
                return false;
            } finally {
                this.loading = false;
            }
        },

        fillWithEntityValues() {
            const entity = this.localValue[this.activeLocationType]?.entity;
            if (!entity) return;
            this.activeAddress.szadrsdesc = entity.name || entity.nome || '';
            this.activeAddress.nome = entity.name || entity.nome || '';
            this.activeAddress.morada = entity.address || entity.morada || '';
            this.activeAddress.local = entity.city || entity.local || '';
            this.activeAddress.codpost = entity.zipCode || entity.zip_code || entity.codpost || '';
            this.activeAddress.pais = entity.country || entity.pais || 'Portugal';
        },

        async fillWithEquipment() {
            if (!this.effectivePatId) return;

            this.loadingEquipment = true;
            try {
                let info = this.patInfo;
                if (!info) {
                    info = await patService.getInfo(this.effectivePatId);
                    this.patInfo = info;
                }

                if (!info) {
                    this.$toast.add({
                        severity: 'warn',
                        summary: 'Aviso',
                        detail: 'Não foi possível obter dados do PAT.',
                        life: 3000
                    });
                    return;
                }

                const eq = info.equipment || {};
                const brand = (eq.brand || '').trim();
                const model = (eq.model || '').trim();
                const serie = (eq.serie || '').trim();

                let desc = [brand, model].filter(Boolean).join(' ');
                if (serie) {
                    desc = desc ? `${desc} (S/N: ${serie})` : `S/N: ${serie}`;
                }
                if (!desc) {
                    desc = (info.oem || '').trim() || 'Equipamento';
                }

                const newLine = {
                    id: String(Math.random().toString(36).slice(2)),
                    product_ref: '',
                    description: desc,
                    quantity: 1,
                    warehouse_origin: null,
                    sale_price: 0,
                    price_cost: 0,
                    discount_value: 0,
                    vat_value: 0,
                    total: 0
                };

                const currentLines = Array.isArray(this.form.lines) ? [...this.form.lines] : [];
                if (currentLines.length === 1 && !currentLines[0].product_ref && !currentLines[0].description) {
                    newLine.id = currentLines[0].id || newLine.id;
                    currentLines[0] = newLine;
                } else {
                    currentLines.push(newLine);
                }

                this.updateField('lines', currentLines);
                this.$toast.add({
                    severity: 'success',
                    summary: 'Sucesso',
                    detail: `Equipamento adicionado: ${desc}`,
                    life: 3000
                });
            } catch (error) {
                console.error('Erro ao preencher com equipamento:', error);
                this.$toast.add({
                    severity: 'error',
                    summary: 'Erro',
                    detail: 'Erro ao obter dados do equipamento.',
                    life: 3000
                });
            } finally {
                this.loadingEquipment = false;
            }
        },

        async openPartsDialog() {
            if (!this.effectivePatId) return;

            this.loadingParts = true;
            try {
                const dossiers = await internalDossierService.getPatDossiers(this.effectivePatId, 5);
                if (!dossiers || !dossiers.length) {
                    this.$toast.add({
                        severity: 'warn',
                        summary: 'Aviso',
                        detail: 'Não foram encontrados documentos de Transferência entre Armazéns (Tipo 5) para este PAT.',
                        life: 3500
                    });
                    return;
                }

                const parts = [];
                dossiers.forEach(d => {
                    if (d.lines && d.lines.length > 0) {
                        d.lines.forEach((l, idx) => {
                            parts.push({
                                ...l,
                                dossier_id: d.id,
                                dossier_name: d.type_name || `Dossier #${d.id}`,
                                dossier_date: d.date,
                                uniqueKey: `${d.id}_${l.id || idx}`
                            });
                        });
                    }
                });

                if (!parts.length) {
                    this.$toast.add({
                        severity: 'warn',
                        summary: 'Aviso',
                        detail: 'Os documentos de Transferência entre Armazéns não contêm linhas de peças.',
                        life: 3500
                    });
                    return;
                }

                this.availableParts = parts;
                this.selectedParts = [...parts];
                this.showPartsDialog = true;
            } catch (error) {
                console.error('Erro ao procurar peças do PAT:', error);
                this.$toast.add({
                    severity: 'error',
                    summary: 'Erro',
                    detail: 'Erro ao procurar peças do PAT.',
                    life: 3000
                });
            } finally {
                this.loadingParts = false;
            }
        },

        confirmAddParts() {
            if (!this.selectedParts || !this.selectedParts.length) return;

            const currentLines = Array.isArray(this.form.lines) ? [...this.form.lines] : [];
            let startIndex = 0;

            if (currentLines.length === 1 && !currentLines[0].product_ref && !currentLines[0].description) {
                currentLines[0] = this.formatPartToLine(this.selectedParts[0], currentLines[0].id);
                startIndex = 1;
            }

            for (let i = startIndex; i < this.selectedParts.length; i++) {
                currentLines.push(this.formatPartToLine(this.selectedParts[i]));
            }

            this.updateField('lines', currentLines);
            this.$toast.add({
                severity: 'success',
                summary: 'Sucesso',
                detail: `${this.selectedParts.length} peça(s) adicionada(s) às linhas.`,
                life: 3000
            });
            this.showPartsDialog = false;
            this.selectedParts = [];
        },

        formatPartToLine(part, existingId = null) {
            return {
                id: existingId || String(Math.random().toString(36).slice(2)),
                product_ref: part.product_ref || '',
                description: part.description || '',
                quantity: (part.quantity !== undefined && part.quantity !== null) ? Number(part.quantity) : 1,
                warehouse_origin: String(part.warehouse_origin),
                cost_price: part.cost_price !== undefined ? Number(part.cost_price) : 0,
                sale_price: part.sale_price !== undefined ? Number(part.sale_price) : 0,
                discount: part.discount !== undefined ? Number(part.discount) : 0,
                discount_value: 0,
                vat: part.tax !== undefined ? Number(part.tax) : 2,
                vat_value: 0,
                total: part.total !== undefined ? Number(part.total) : 0
            };
        }
    }
};
</script>

<style scoped>
.address-section {
    background-color: #ffffff;
    border: 1px solid #dee2e6;
    transition: transform 0.2s;
    height: 100%;
}

.load-section {
    border-top: 5px solid #9b71e3;
    background-color: #fefdff;
}

.unload-section {
    border-top: 5px solid #fbc02d;
    background-color: #fffdf9;
}

.address-summary {
    background: #ffffff;
    border-left: 3px solid #6c757d;
    box-shadow: inset 0 0 5px rgba(0, 0, 0, 0.05);
}

.text-primary { color: #9b71e3; }
.text-warning { color: #fbc02d; }

h3 {
    font-size: 1.1rem;
    font-weight: 600;
    margin-top: 0;
    display: flex;
    align-items: center;
}

.p-button-auto {
    width: auto;
}
</style>